import type { Item } from '../equipment';
import { certificationForItemLevel, type GuildRank } from '../progression/guild';
import { equipmentTemplateFromItem, isEquipmentTemplateItem } from './catalog';
import type { EquipmentInstance, EquipmentRarity } from './procedural';

const RARITIES = new Set<EquipmentRarity>(['common', 'rare', 'epic', 'legendary']);
const MIGRATION_VERSION = 2;

const LEGACY_MAX_PER_TEMPLATE = 20;
const LEGACY_MAX_TOTAL = 200;
function stableHash(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function legacyStats(item: Item): Record<string, number> {
  const stats: Record<string, number> = {};
  for (const [key, raw] of Object.entries(item.stats || {})) {
    const value = Number(raw);
    if (Number.isFinite(value)) stats[key] = value;
  }
  return stats;
}

/**
 * Convertit les objets fixes possédés en instances liées, de façon déterministe
 * et idempotente. La fonction est pure vis-à-vis du catalogue mais mute l'état
 * fourni, ce qui permet de l'utiliser aussi bien au chargement client que côté
 * serveur avant le garde-fou de sauvegarde.
 */
export function migrateLegacyEquipmentState(
  gameState: any,
  ownerId: string,
  catalog: Record<string, Item>,
): EquipmentInstance[] {
  if (!gameState || typeof gameState !== 'object') return [];
  if (!Array.isArray(gameState.equipmentInventory)) gameState.equipmentInventory = [];
  if (!gameState.equipment || typeof gameState.equipment !== 'object') gameState.equipment = {};

  const safeOwner = String(ownerId || 'legacy').slice(0, 160);
  const desiredCount = new Map<string, number>();
  // Les mêmes possessions étaient historiquement recopiées dans plusieurs
  // champs. On additionne les doublons à l'intérieur de chaque source, puis on
  // prend le maximum entre sources miroir pour ne ni dupliquer ni perdre.
  const equippedCounts = new Map<string, number>();
  const ownedCounts = new Map<string, number>();
  const inventoryCounts = new Map<string, number>();
  const accumulate = (target: Map<string, number>, templateId: unknown, count = 1) => {
    if (typeof templateId !== 'string' || !templateId || templateId === 'none' || templateId === '__two_handed__') return;
    const item = catalog[templateId];
    if (!item || !equipmentTemplateFromItem(item)) return;
    const safeCount = Math.min(LEGACY_MAX_PER_TEMPLATE, Math.max(1, Math.floor(Number(count) || 1)));
    target.set(templateId, Math.min(LEGACY_MAX_PER_TEMPLATE, (target.get(templateId) || 0) + safeCount));
  };
  for (const reference of Object.values(gameState.equipment)) accumulate(equippedCounts, reference);
  for (const reference of Array.isArray(gameState.ownedCosmetics) ? gameState.ownedCosmetics : []) {
    accumulate(ownedCounts, reference);
  }
  for (const entry of Array.isArray(gameState.inventory?.items) ? gameState.inventory.items : []) {
    accumulate(inventoryCounts, entry?.id ?? entry?.itemId, entry?.qty ?? entry?.quantity ?? 1);
  }
  const templateIds = new Set([...equippedCounts.keys(), ...ownedCounts.keys(), ...inventoryCounts.keys()]);
  for (const templateId of templateIds) {
    desiredCount.set(templateId, Math.max(
      equippedCounts.get(templateId) || 0, ownedCounts.get(templateId) || 0, inventoryCounts.get(templateId) || 0,
    ));
  }
  let remainingLegacyInstances = LEGACY_MAX_TOTAL;
  for (const [templateId, count] of desiredCount) {
    if (remainingLegacyInstances <= 0) {
      desiredCount.delete(templateId);
      continue;
    }
    const accepted = Math.min(count, remainingLegacyInstances);
    desiredCount.set(templateId, accepted);
    remainingLegacyInstances -= accepted;
  }
  // Une fois converti en instances, un équipement ne doit plus subsister sous
  // forme de pile legacy : cela affichait deux possessions et permettait à une
  // sauvegarde suivante de recréer la pile.
  const migratedTemplateIds = new Set(desiredCount.keys());
  if (Array.isArray(gameState.inventory?.items)) {
    gameState.inventory.items = gameState.inventory.items.filter((entry: any) => !migratedTemplateIds.has(entry?.id ?? entry?.itemId));
  }
  if (Array.isArray(gameState.ownedCosmetics)) {
    gameState.ownedCosmetics = gameState.ownedCosmetics.filter((reference: unknown) => !migratedTemplateIds.has(String(reference)));
  }
  const existingByTemplate = new Map<string, EquipmentInstance[]>();
  for (const instance of gameState.equipmentInventory as EquipmentInstance[]) {
    if (!instance?.legacy || typeof instance.templateId !== 'string') continue;
    // Les toutes premieres migrations locales ne connaissaient pas encore
    // l'identifiant du compte et inscrivaient `legacy-local`. Conserver cet
    // owner faisait ensuite rejeter ces pieces par le garde-fou serveur, qui
    // nettoyait aussi les slots actifs : le heros apparaissait nu au prochain
    // round-trip (souvent pendant le chargement d'un donjon). L'instance reste
    // la meme afin de ne casser aucune reference de tenue ou de build.
    if (instance.ownerId !== safeOwner) instance.ownerId = safeOwner;
    const list = existingByTemplate.get(instance.templateId) || [];
    list.push(instance);
    existingByTemplate.set(instance.templateId, list);
  }

  for (const [templateId, count] of desiredCount) {
    const item = catalog[templateId]!;
    const template = equipmentTemplateFromItem(item)!;
    const existing = existingByTemplate.get(templateId) || [];
    for (let index = existing.length; index < count; index++) {
      const itemLevel = Math.max(1, Math.min(200, Math.floor(Number(item.levelReq) || 1)));
      const instance: EquipmentInstance = {
        instanceId: `legacy_${stableHash(`${safeOwner}:${templateId}:${index}`)}`,
        templateId,
        ownerId: safeOwner,
        slot: template.slot,
        itemLevel,
        rarity: RARITIES.has(item.rarity as EquipmentRarity) ? item.rarity as EquipmentRarity : 'common',
        qualityStars: 3,
        affixes: [],
        requiredCertification: certificationForItemLevel(itemLevel) as GuildRank,
        bound: true,
        source: { kind: 'legacy' },
        generatedAt: 0,
        legacy: true,
        legacyStats: legacyStats(item),
      };
      gameState.equipmentInventory.push(instance);
      existing.push(instance);
    }
    existingByTemplate.set(templateId, existing);
  }

  for (const slot of Object.keys(gameState.equipment)) {
    const reference = gameState.equipment[slot];
    if (typeof reference !== 'string' || !desiredCount.has(reference)) continue;
    const replacement = existingByTemplate.get(reference)?.find((instance) => instance.slot === slot)
      || existingByTemplate.get(reference)?.[0];
    if (replacement) gameState.equipment[slot] = replacement.instanceId;
  }

  gameState._equipmentMigrationVersion = MIGRATION_VERSION;
  return gameState.equipmentInventory;
}

/**
 * Une inscription ne constitue pas une preuve de possession : toutes les
 * instances et références d'équipement fournies par le client sont retirées.
 * Les cosmétiques sans statistiques et les consommables restent hors périmètre.
 */
export function stripUntrustedEquipmentState(gameState: any, catalog: Record<string, Item>): void {
  if (!gameState || typeof gameState !== 'object') return;
  gameState.equipmentInventory = [];
  if (gameState.equipment && typeof gameState.equipment === 'object') {
    for (const slot of Object.keys(gameState.equipment)) gameState.equipment[slot] = null;
  }
  if (Array.isArray(gameState.player?.savedBuilds)) {
    for (const build of gameState.player.savedBuilds) {
      if (build && typeof build === 'object') build.equipment = {};
    }
  }
  if (Array.isArray(gameState.inventory?.items)) {
    gameState.inventory.items = gameState.inventory.items.filter((entry: any) => {
      const item = catalog[String(entry?.id ?? entry?.itemId ?? '')];
      return !item || !isEquipmentTemplateItem(item);
    });
  }
  if (Array.isArray(gameState.ownedCosmetics)) {
    gameState.ownedCosmetics = gameState.ownedCosmetics.filter((reference: unknown) => {
      const item = catalog[String(reference ?? '')];
      return !item || !isEquipmentTemplateItem(item);
    });
  }
  gameState._equipmentMigrationVersion = MIGRATION_VERSION;
}
